// Ported from genieos/web/src/components/historian/StatusBar.tsx @ 84923d036
/**
 * StatusBar - Query metadata display for historian data viewer
 *
 * Shows selected variable count, time range, interval, and query statistics
 * including point count, query time, and downsampling ratio.
 */

import React from 'react';
import { Clock, Database, Zap, AlertTriangle, Loader2 } from 'lucide-react';
import { useHistorian } from '../../contexts/HistorianContext';
import { HISTORIAN_MAX_SERIES } from './historianConstants';

interface StatusBarProps {
  className?: string;
}

export const StatusBar: React.FC<StatusBarProps> = ({ className }) => {
  const { state } = useHistorian();
  const {
    selectedVariables,
    queryResult,
    queryLoading,
    queryError,
    timeRange,
    interval,
  } = state;

  // Format time range for display
  const formatTimeRange = () => {
    const formatOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };

    const start = timeRange.start.toLocaleString('en-US', formatOptions);
    const end = timeRange.end.toLocaleString('en-US', formatOptions);
    return `${start} → ${end}`;
  };

  // Calculate downsampling ratio
  const downsampleRatio =
    queryResult?.metadata &&
    queryResult.metadata.downsampledFrom > 0 &&
    queryResult.metadata.pointCount < queryResult.metadata.downsampledFrom
      ? (
          (1 -
            queryResult.metadata.pointCount /
              queryResult.metadata.downsampledFrom) *
          100
        ).toFixed(1)
      : null;

  return (
    <div
      className={`flex items-center justify-between text-xs text-ems-text-secondary border-t border-ems-border pt-3 ${className}`}
    >
      {/* Left side - Selection and Time */}
      <div className="flex items-center gap-4">
        <span className="flex items-center gap-1.5">
          <Database className="w-3 h-3" />
          Variables: {selectedVariables.length}/{HISTORIAN_MAX_SERIES}
        </span>
        <span className="flex items-center gap-1.5">
          <Clock className="w-3 h-3" />
          {formatTimeRange()}
        </span>
        <span>
          Interval: <span className="text-ems-text-primary">{interval}</span>
        </span>
      </div>

      {/* Right side - Query Stats */}
      <div className="flex items-center gap-4">
        {queryLoading && (
          <span className="flex items-center gap-1.5 text-ems-blue animate-pulse">
            <Loader2 className="w-3 h-3 animate-spin" />
            Loading...
          </span>
        )}

        {queryError && !queryLoading && (
          <span className="flex items-center gap-1.5 text-ems-red">
            <AlertTriangle className="w-3 h-3" />
            Query failed
          </span>
        )}

        {queryResult && !queryLoading && !queryError && (
          <>
            <span className="flex items-center gap-1.5">
              <Zap className="w-3 h-3" />
              {queryResult.metadata.pointCount.toLocaleString()} points
            </span>
            <span>
              Query:{' '}
              <span className="text-ems-text-primary">
                {queryResult.metadata.queryTimeMs}ms
              </span>
            </span>
            {downsampleRatio && parseFloat(downsampleRatio) > 0 && (
              <span>
                Downsampled:{' '}
                <span className="text-ems-green">{downsampleRatio}%</span>
              </span>
            )}
            <span className="text-ems-text-secondary/50">
              ({queryResult.metadata.interval} intervals)
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default StatusBar;
